import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Card, Dialog, Input, TabNotPorted, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ProductLink } from "../../components/ProductLink";
import {
  LazyProductClient,
  ProductClient,
  type LazyProductDTO,
  type ProductDTO,
} from "../../api/product";

/**
 * A bundle's component items.
 *
 * Membership and quantities persist immediately — a bundle whose composition is
 * wrong is wrong for pricing, dispatch and feeds all at once, so these writes
 * do not wait behind the page Save the way field edits do. That matches the
 * Vaadin grid exactly.
 */
export function ItemsTab({
  draft,
  readOnly,
  onPersisted,
}: {
  draft: ProductDTO;
  readOnly: boolean;
  /** Called after any immediate write so the page can refetch the product. */
  onPersisted: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const products = useMemo(() => new ProductClient(http), [http]);
  const lazy = useMemo(() => new LazyProductClient(http), [http]);

  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const lines = draft.group_lines ?? [];
  const itemIds = lines
    .map((line) => line.product_id)
    .filter((id): id is number => typeof id === "number");

  const { data: items } = useQuery({
    queryKey: ["product", "bundle-items", draft.id, itemIds.join(",")],
    queryFn: () => products.findByIds(itemIds),
    enabled: itemIds.length > 0,
  });

  const persist = async (nextLines: ProductDTO["group_lines"]) => {
    setSaving(true);
    try {
      await products.updateProduct({ ...draft, group_lines: nextLines });
      await queryClient.invalidateQueries({ queryKey: ["product", draft.id] });
      onPersisted();
      toast.success(t("notification.saved", "Saved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    } finally {
      setSaving(false);
    }
  };

  const setQuantity = (memberId: number, quantity: number) => {
    if (quantity < 1) return;
    void persist(
      lines.map((line) => (line.product_id === memberId ? { ...line, quantity } : line)),
    );
  };

  const removeItem = async (item: ProductDTO) => {
    const confirmed = await confirm({
      title: t("product.items.remove.title", "Remove this item from the bundle?"),
      message: item.metadata?.sku ?? "",
      variant: "danger",
    });
    if (!confirmed) return;
    void persist(lines.filter((line) => line.product_id !== item.id));
  };

  const addItems = (selected: LazyProductDTO[]) => {
    const additions = selected.map((product) => ({
      id: 0,
      master_product_id: draft.id,
      product_id: product.id,
      quantity: 1,
    }));
    setAdding(false);
    void persist([...lines, ...additions]);
  };

  return (
    <div className="flex flex-col gap-3">
      {!readOnly && (
        <div>
          <Button
            size="sm"
            variant="primary"
            icon={<Plus className="size-4" />}
            disabled={saving}
            onClick={() => setAdding(true)}
          >
            {t("product.items.add", "Add items")}
          </Button>
        </div>
      )}

      {itemIds.length === 0 ? (
        <TabNotPorted message={t("product.items.none", "This bundle has no items yet.")} />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-line bg-surface-sunk text-[11px] uppercase tracking-wider text-ink-faint">
                <th className="px-4 py-2 text-left">{t("column.sku", "SKU")}</th>
                <th className="px-4 py-2 text-left">{t("column.name", "Name")}</th>
                <th className="px-4 py-2 text-left">{t("column.status", "Status")}</th>
                <th className="px-4 py-2 text-right">{t("column.qty", "Qty available")}</th>
                <th className="px-4 py-2 text-right">{t("column.cogs", "COGS")}</th>
                <th className="px-4 py-2 text-right">{t("product.items.quantity", "In bundle")}</th>
                {!readOnly && <th className="w-12 px-4 py-2" />}
              </tr>
            </thead>
            <tbody>
              {(items ?? []).map((item) => {
                const line = lines.find((entry) => entry.product_id === item.id);
                return (
                  <tr key={item.id} className="border-b border-line-soft last:border-0">
                    <td className="px-4 py-2">
                      <Link
                        to="/products/$productId"
                        params={{ productId: String(item.id) }}
                        className="font-medium text-accent hover:underline"
                      >
                        <ProductLink id={item.id}>{item.metadata?.sku ?? ""}</ProductLink>
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-ink">
                      {item.description_en?.product_name ?? ""}
                    </td>
                    <td className="px-4 py-2">
                      <Badge
                        variant={item.status === "AVAILABLE" ? "success" : "warning"}
                        size="sm"
                      >
                        {item.status}
                      </Badge>
                    </td>
                    <td className="tabular px-4 py-2 text-right">
                      {item.kpi_inventory
                        ? item.kpi_inventory.quantity - item.kpi_inventory.reserved_quantity
                        : ""}
                    </td>
                    <td className="tabular px-4 py-2 text-right">{item.pricing?.cogs ?? ""}</td>
                    <td className="px-4 py-2 text-right">
                      <Input
                        type="number"
                        min={1}
                        value={line?.quantity ?? 1}
                        readOnly={readOnly}
                        disabled={saving}
                        onChange={(event) => {
                          const quantity = Number(event.target.value);
                          if (!Number.isNaN(quantity)) setQuantity(item.id, quantity);
                        }}
                        className="h-7 w-20 text-right"
                      />
                    </td>
                    {!readOnly && (
                      <td className="px-4 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="size-7 p-0"
                          aria-label={t("product.items.remove", "Remove")}
                          disabled={saving}
                          onClick={() => void removeItem(item)}
                          icon={<Trash2 className="size-3.5" />}
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {adding && (
        <ItemPickerDialog
          onClose={() => setAdding(false)}
          onConfirm={addItems}
          excludeIds={[draft.id, ...itemIds]}
          lazy={lazy}
        />
      )}
    </div>
  );
}

/**
 * Candidate picker: single items and components, minus what's already in the
 * bundle — a bundle can't contain another bundle or itself.
 */
function ItemPickerDialog({
  onClose,
  onConfirm,
  excludeIds,
  lazy,
}: {
  onClose: () => void;
  onConfirm: (selected: LazyProductDTO[]) => void;
  excludeIds: number[];
  lazy: LazyProductClient;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["product", "bundle-candidates"],
    queryFn: async () => {
      const [singles, components] = await Promise.all([
        lazy.byType("SINGLE_ITEM"),
        lazy.byType("COMPONENT"),
      ]);
      return [...singles, ...components];
    },
    staleTime: 60_000,
  });

  const needle = query.trim().toLowerCase();
  const candidates = (data ?? [])
    .filter((product) => !excludeIds.includes(product.id))
    .filter(
      (product) =>
        needle === "" ||
        `${product.metadata?.sku ?? ""} ${product.description_en?.product_name ?? ""}`
          .toLowerCase()
          .includes(needle),
    )
    .slice(0, 200);

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("product.items.add", "Add items")}
      description={t("product.items.add.description", "Single items and components only.")}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={() => onConfirm((data ?? []).filter((product) => selected.has(product.id)))}
          >
            {t("button.add", "Add")} ({selected.size})
          </Button>
        </>
      }
    >
      <Input
        value={query}
        autoFocus
        placeholder={t("grid.search", "Search")}
        onChange={(event) => setQuery(event.target.value)}
      />

      {isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : (
        <ul className="mt-2 flex flex-col">
          {candidates.map((product) => (
            <li key={product.id}>
              <label className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-surface-sunk">
                <input
                  type="checkbox"
                  checked={selected.has(product.id)}
                  onChange={() => toggle(product.id)}
                  className="size-3.5 accent-[var(--accent)]"
                />
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-medium text-ink">
                    <ProductLink id={product.id}>{product.metadata?.sku ?? ""}</ProductLink>
                  </span>
                  <span className="block truncate text-[12px] text-ink-muted">
                    {product.description_en?.product_name ?? ""}
                  </span>
                </span>
                <Badge variant="neutral" size="sm" className="ml-auto shrink-0">
                  {product.type}
                </Badge>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
